import {normalizePrice, priceFormat} from './price'
import type {PriceFormat} from './price'
import {createOrderForm} from './orderForm'
import type {OrderForm} from './orderForm'
import {buttonColor} from './buttonColor'
import {parseCrop} from './image'
import type {TariffImage} from './image'

export const MAX_TARIFFS = 10
export const MAX_ADVANTAGES = 10
export const ADVANTAGE_MARKERS = ['check', 'dot', 'dot_outline', 'arrow', 'star', 'sparkle', 'plus', 'minus'] as const
export type AdvantageMarker = typeof ADVANTAGE_MARKERS[number]
export const advantageMarker = (value: unknown): AdvantageMarker =>
    ADVANTAGE_MARKERS.includes(value as AdvantageMarker) ? value as AdvantageMarker : 'check'
export type Advantage = {id: number; text: string}

export const FIXED_FIELDS = ['image', 'name'] as const
export const MOVABLE_FIELDS = ['description', 'advantages', 'price', 'button', 'additionalText'] as const
export const FIELD_KEYS = [...FIXED_FIELDS, ...MOVABLE_FIELDS] as const
export const TEXT_FIELDS = ['name', 'description', 'additionalText'] as const

export type MovableFieldKey = typeof MOVABLE_FIELDS[number]
export type TariffFieldKey = typeof FIELD_KEYS[number]
export type TariffField = {
  enabled: boolean
  value: string
  items?: Advantage[]
  format?: PriceFormat
  oldPrice?: {enabled: boolean; value: string}
  buttonStyle?: {color: string; radius: 'no' | 'default' | 'big'}
  image?: TariffImage
}
export type Tariff = {
  id: number
  fields: Record<TariffFieldKey, TariffField>
  order: MovableFieldKey[]
  orderForm: OrderForm
  photo?: number | null
}

let nextId = Date.now()
export const createAdvantage = (text = ''): Advantage => ({id: ++nextId, text})

export const createTariff = (source?: Tariff): Tariff => {
  const fields = Object.fromEntries(FIELD_KEYS.map(key => [key, {
    enabled: source?.fields?.[key]?.enabled === true,
    value: typeof source?.fields?.[key]?.value === 'string' ? source.fields[key].value : ''
  }])) as Tariff['fields']
  const image = source?.fields?.image?.image
  const imageId = image?.imageId ?? (source?.fields?.image ? undefined : source?.photo)
  if (image || (typeof imageId === 'number' && imageId > 0)) {
    fields.image.image = {
      ...image, imageId: imageId ?? undefined,
      mimeType: image?.mimeType === 'image/png' ? 'image/png' : 'image/jpeg',
      crop: parseCrop(image?.crop)
    }
  }
  const advantages = source?.fields?.advantages
  fields.advantages.items = Array.isArray(advantages?.items)
      ? advantages.items.slice(0, MAX_ADVANTAGES).map(item => createAdvantage(typeof item.text === 'string' ? item.text : ''))
      : [createAdvantage(fields.advantages.value)]
  // The list is now the source of truth; migrate the old free-text field once.
  fields.advantages.value = ''
  if (!fields.advantages.items.length) fields.advantages.items.push(createAdvantage())
  const price = source?.fields?.price
  fields.price.value = normalizePrice(fields.price.value) ?? fields.price.value
  fields.price.format = priceFormat(price?.format)
  const oldValue = typeof price?.oldPrice?.value === 'string' ? price.oldPrice.value : ''
  fields.price.oldPrice = {enabled: price?.oldPrice?.enabled === true, value: normalizePrice(oldValue) ?? oldValue}
  const buttonStyle = source?.fields?.button?.buttonStyle
  fields.button.buttonStyle = {
    color: typeof buttonStyle?.color === 'string' && buttonStyle.color ? buttonStyle.color : '#0D87EF',
    radius: buttonStyle?.radius === 'no' || buttonStyle?.radius === 'big' ? buttonStyle.radius : 'default'
  }
  const savedOrder = Array.isArray(source?.order) ? source.order : []
  const order = [...new Set([...savedOrder, ...MOVABLE_FIELDS])]
      .filter(key => MOVABLE_FIELDS.includes(key))

  return { id: source?.id ?? ++nextId, fields, order, orderForm: createOrderForm(source?.orderForm), photo: imageId ?? null }
}

export const hasFilledElement = (tariff: Tariff): boolean =>
    FIELD_KEYS.some(key => tariff.fields[key].enabled && (key === 'advantages'
      ? tariff.fields.advantages.items?.some(item => item.text.trim().length > 0)
      : key === 'image' ? hasTariffImage(tariff.fields.image)
      : tariff.fields[key].value.trim().length > 0))

export const hasTariffImage = (field: TariffField): boolean => !!field.value.trim()
  || !!field.image?.imageId || !!field.image?.editedSrc

export const hasButtonDraft = (tariff: Tariff): boolean => !!tariff.fields.button.value.trim()
  || (tariff.fields.button.buttonStyle?.radius ?? 'default') !== 'default'
  || buttonColor(tariff.fields.button.buttonStyle?.color).css !== buttonColor('#0D87EF').css
  || JSON.stringify(createOrderForm(tariff.orderForm)) !== JSON.stringify(createOrderForm())

export const hasTariffDraft = (tariff: Tariff): boolean =>
    FIELD_KEYS.some(key => tariff.fields[key].enabled || !!tariff.fields[key].value.trim())
    || hasTariffImage(tariff.fields.image) || !!tariff.fields.image.image?.originalSrc
    || !!tariff.fields.advantages.items?.some(item => item.text.trim())
    || !!tariff.fields.price.oldPrice?.enabled || !!tariff.fields.price.oldPrice?.value.trim()
    || (tariff.fields.price.format ?? '₸') !== '₸'
    || hasButtonDraft(tariff)
    || JSON.stringify(tariff.order) !== JSON.stringify(MOVABLE_FIELDS)

export const tariffsToSave = (tariffs: Tariff[], retainedIds = new Set<number>()): Tariff[] => {
  // Keep disabled text, including when it is the tariff's only populated field.
  return tariffs.filter(tariff => retainedIds.has(tariff.id) || hasFilledElement(tariff)
      || hasTariffImage(tariff.fields.image)
      || tariff.fields.price.value.trim() || tariff.fields.price.oldPrice?.value.trim()
      || tariff.fields.advantages.items?.some(item => item.text.trim())
      || hasButtonDraft(tariff)
      || TEXT_FIELDS.some(key => tariff.fields[key].value.trim().length > 0))
      .slice(0, MAX_TARIFFS).map(tariff => {
        const saved = createTariff(tariff)
        saved.fields.advantages.items = saved.fields.advantages.items!.filter(item => item.text.trim())
        return saved
      })
}
